import type { BulkResult, FilterSpec, Meta, SpecimenListInput } from '@numistic/contracts'
import { useBulkSpecimens, useMeta, useSpecimenList, useTree } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { Alert, Button, Checkbox, Select, cn } from '@numistic/ui'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'

import { MutationErrors } from '../components/mutation-errors.js'
import { GridSkeleton } from '../components/grid-skeleton.js'
import { useErrorText } from '../lib/errors.js'
import { BulkFieldEditor } from '../meta/bulk-field.js'
import { useRefResolver } from '../meta/ref-resolver.js'

/** Список экземпляров: плитка с выбором для массовых операций. */
export function SpecimenList({
  query,
}: {
  query: Omit<SpecimenListInput, 'cursor' | 'filter'> & { filter?: FilterSpec }
}) {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const meta = useMeta().data
  const { filter, ...rest } = query
  const complete =
    filter?.and.filter(
      (c) => c.value !== undefined || c.op === 'is_set' || c.op === 'is_not_set',
    ) ?? []
  const list = useSpecimenList({
    ...rest,
    filter: complete.length ? JSON.stringify({ and: complete }) : undefined,
    limit: 50,
  })
  const items = list.data?.pages.flatMap((p) => p.items) ?? []
  const [selected, setSelected] = useState<Set<string>>(new Set())
  // Другая выборка (коллекция, поиск, фильтр) — прежнее выделение к ней не относится.
  const queryKey = JSON.stringify(query)
  const [selectionKey, setSelectionKey] = useState(queryKey)
  if (selectionKey !== queryKey) {
    setSelectionKey(queryKey)
    setSelected(new Set())
  }
  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const grid = 'grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4'
  if (list.error) return <Alert variant="destructive">{errorText.request(list.error)}</Alert>
  if (list.isPending || !meta) return <GridSkeleton className={grid} />
  if (items.length === 0)
    return (
      <div className="grid justify-items-start gap-3 rounded-lg border border-dashed p-6">
        <p className="text-sm text-muted-foreground">{t('collections.empty')}</p>
        <Button asChild size="sm">
          <Link to="/s/new" search={{ collection: query.collectionId }}>
            {t('collections.addSpecimen')}
          </Link>
        </Button>
      </div>
    )
  return (
    <div className="grid gap-3">
      {selected.size ? (
        <BulkBar meta={meta} ids={[...selected]} onDone={() => setSelected(new Set())} />
      ) : null}
      <ul className={grid} aria-label={t('collections.title')}>
        {items.map((item) => (
          <SpecimenCard
            key={item.id}
            meta={meta}
            item={item}
            selected={selected.has(item.id)}
            selecting={selected.size > 0}
            onToggle={() => toggle(item.id)}
          />
        ))}
      </ul>
      {list.hasNextPage ? (
        <Button
          variant="outline"
          className="justify-self-center"
          onClick={() => void list.fetchNextPage()}
        >
          {t('catalog.more')}
        </Button>
      ) : null}
    </div>
  )
}

function SpecimenCard({
  meta,
  item,
  selected,
  selecting,
  onToggle,
}: {
  selecting: boolean
  meta: Meta
  item: { id: string; title: string; thumb: string | null; values: Record<string, unknown> }
  selected: boolean
  onToggle: () => void
}) {
  const resolve = useRefResolver(meta, new Set(['issuers']))
  const status = meta.statuses.find((s) => s.id === item.values.status)
  const grade = item.values.grade as { scale: string; value: string } | undefined
  return (
    <li
      className={cn(
        'group/card relative rounded-lg border bg-card p-2 transition-colors hover:border-primary/60',
        selected && 'ring-2 ring-primary',
      )}
    >
      <Checkbox
        aria-label={item.title}
        checked={selected}
        onChange={onToggle}
        className={cn(
          'absolute left-3 top-3 z-10 bg-card',
          // На устройствах с мышью чекбокс появляется при наведении или в режиме выбора.
          !selecting &&
            '[@media(hover:hover)]:opacity-0 [@media(hover:hover)]:focus:opacity-100 [@media(hover:hover)]:group-hover/card:opacity-100',
        )}
      />
      <Link to="/s/$id" params={{ id: item.id }} className="group grid gap-2">
        <div className="flex aspect-square items-center justify-center overflow-hidden rounded-md bg-muted">
          {item.thumb ? (
            <img src={item.thumb} alt="" loading="lazy" className="size-full object-contain" />
          ) : null}
        </div>
        <div className="grid gap-1 px-1 pb-1">
          <span className="line-clamp-2 text-sm font-medium group-hover:text-primary">
            {item.title}
          </span>
          <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {resolve('issuers', String(item.values.issuer ?? ''))}
            {grade ? <span>{grade.value}</span> : null}
            {status && status.code !== 'in_collection' ? (
              <span
                className="rounded px-1.5"
                style={{ backgroundColor: `${status.color}22`, color: status.color }}
              >
                {status.name.ru}
              </span>
            ) : null}
            {Number(item.values.quantity) > 1 ? <span>×{String(item.values.quantity)}</span> : null}
          </span>
        </div>
      </Link>
    </li>
  )
}

function BulkBar({ meta, ids, onDone }: { meta: Meta; ids: string[]; onDone: () => void }) {
  const { t } = useTranslation()
  const bulk = useBulkSpecimens()
  const [result, setResult] = useState<BulkResult | null>(null)
  const tree = useTree('collections').data ?? []
  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-lg border bg-accent/40 p-2 text-sm"
      role="toolbar"
    >
      <span>{t('collections.selected', { count: ids.length })}</span>
      <Select
        className="w-56"
        aria-label={t('collections.bulkMove')}
        value=""
        onChange={(e) =>
          e.target.value &&
          bulk.mutate({ ids, collectionId: e.target.value }, { onSuccess: onDone })
        }
      >
        <option value="">{t('collections.bulkMove')}</option>
        {tree.map((n) => (
          <option key={n.id} value={n.id}>
            {n.name}
          </option>
        ))}
      </Select>
      <Select
        className="w-56"
        aria-label={t('collections.bulkStatus')}
        value=""
        onChange={(e) =>
          e.target.value && bulk.mutate({ ids, status: e.target.value }, { onSuccess: onDone })
        }
      >
        <option value="">{t('collections.bulkStatus')}</option>
        {meta.statuses
          .filter((s) => s.active !== false)
          .map((s) => (
            <option key={s.code} value={s.code}>
              {s.name.ru}
            </option>
          ))}
      </Select>
      <BulkFieldEditor
        meta={meta}
        level="specimen"
        exclude={['collection', 'status']}
        pending={bulk.isPending}
        onApply={(values) => bulk.mutate({ ids, values }, { onSuccess: setResult })}
      />
      <Button variant="ghost" size="sm" onClick={onDone}>
        {t('common.cancel')}
      </Button>
      {result ? (
        <span role="status" className="w-full text-xs text-muted-foreground">
          {t('bulk.result', { updated: result.updated })}
          {result.failed.length ? ` ${t('bulk.failed', { count: result.failed.length })}` : ''}
        </span>
      ) : null}
      <div className="w-full">
        <MutationErrors errors={[bulk.error]} />
      </div>
    </div>
  )
}
